/* ═══════════════════════════════════════════════════════
   suite-io — Files tab (crew view)
   ═══════════════════════════════════════════════════════
   Ported from legacy nyc-suite (JobsPanel "FILES (crew)" section).
   Read-only: crew see ONLY the files an admin marked crew-visible
   on the Docs & Files tab, with a download button. No upload, no
   toggle, no delete.
*/

import { useState, useEffect } from "react";
import { C, font, lbl } from "../../../../lib/styles.js";
import { dataService } from "../../../../lib/dataService.js";
import { useDataOptional } from "../../../../context/DataContext.jsx";
import { fmtDate, FMT_DATE_DOW } from "../../../../lib/utils.js";
import { FileRow } from "./DocsFilesTab.jsx";

export default function CrewFilesTab({ job }) {
  const [jobFiles, setJobFiles] = useState([]);
  const crewCards = useDataOptional()?.crewCards;
  // Deployed crew cards for this job (members can read crewCards; the link
  // itself is the same no-login page the email carries).
  const sessionDate = (sid) => (job?.sessions || []).find(s => s.id === sid)?.date || job?.captureDate || "";
  // Links expire 3 days after the session; an expired card is not offered.
  const nowIso = new Date().toISOString();
  const expiresOf = (c) => {
    if (c.lastDeploy?.expiresAt) return c.lastDeploy.expiresAt;
    const d = sessionDate(c.sessionId);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return "";
    const [y, m, day] = d.split("-").map(Number);
    // End of the third day after the session, in the viewer's local time.
    return new Date(y, m - 1, day + 3, 23, 59, 59).toISOString();
  };
  const cards = (crewCards || []).filter(c => c.jobId === job?.id && c.lastDeploy?.url && !c.lastDeploy?.revokedAt && (!expiresOf(c) || expiresOf(c) >= nowIso));

  useEffect(() => {
    if (!job?.id) { setJobFiles([]); return; }
    let cancelled = false;
    (async () => {
      // Read-only merge — crew must never trigger the migration write.
      const merged = await dataService.files.loadMerged(job.id, job.jobId);
      if (!cancelled) setJobFiles(merged);
    })();
    return () => { cancelled = true; };
  }, [job?.id]);

  const crewFiles = (jobFiles || []).filter(f => f.crewVisible);

  return (
    <div style={{ fontFamily: font }}>
      {cards.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ ...lbl, marginBottom: 6, fontSize: 10 }}>Call sheet</div>
          {cards.map(c => (
            <a key={c.id} href={c.lastDeploy.url} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", borderRadius: 4, background: `${C.border}15`, border: `1px solid ${C.border}22`, fontSize: 11, color: C.text, textDecoration: "none", marginBottom: 2 }}>
              <span>🗂</span>
              <span style={{ flex: 1 }}>{sessionDate(c.sessionId) ? fmtDate(sessionDate(c.sessionId), FMT_DATE_DOW) : "Session"} · v{c.lastDeploy.version}</span>
              <span style={{ fontSize: 9, color: C.muted }}>Open</span>
            </a>
          ))}
        </div>
      )}
      <div style={{ ...lbl, marginBottom: 6, fontSize: 10 }}>Files {crewFiles.length > 0 && `(${crewFiles.length})`}</div>
      {crewFiles.length > 0
        ? crewFiles.map((f, i) => <FileRow key={i} file={f} />)
        : <div style={{ fontSize: 11, color: C.muted }}>No files shared with crew</div>}
    </div>
  );
}
